import React, { useEffect, useState } from "react";
import {
  SafeAreaView,
  ScrollView,
  View,
  Dimensions,
  Image,
  Platform,
  ActivityIndicator,
} from "react-native";
import { useChargerForm } from "../hooks/useChargerForm";
import ChargerForm from "../components/ChargerForm/ChargerForm";
import PopupModalWrapper from "../components/ChargerForm/PopupModalWrapper";
import StripeSubscriptionHandler from "../components/Stripe/StripeSubscriptionHandler";
import Header from "../components/Header";
import { loadChargerThumbnail, invalidateCacheEntry, getCachedImageUri } from "../services/charger-image-cache-service";

const { height } = Dimensions.get("window");
interface RegisterUpdateChargerProps {
  navigation: any;
  route: any;
}
const RegisterUpdateCharger: React.FC<RegisterUpdateChargerProps> = ({
  navigation,
  route,
}) => {
  // Check route params DIRECTLY for initialization (before hooks run)
  const isEditMode = Boolean(route.params?.editCharger);
  
  const {
    form,
    errors,
    isUpdating,
    isEnabled,
    isToggleLoading,
    editChargerStatus,
    popupState,
    verified,
    handlers,
    submitCreate,
    submitUpdate,
    setVerified,
    billingType,
    residentialRatePeak,
    residentialRateOffPeak,
    rawPeak,
    rawOffPeak,
    peakSelection,
    showDisableWarning,
    handleDisableConfirm,
    handleDisableCancel,
    disableWarningData,
    supportsScheduling,
    schedulingEnabled,
    isSchedulingPrefLoading,
    toggleSchedulingPreference,
  } = useChargerForm();

  // State for custom charger image - SIMPLIFIED
  const [chargerImageUri, setChargerImageUri] = useState<string | null>(null);
  const [imageLoading, setImageLoading] = useState<boolean>(isEditMode); // Only load if editing
  const [imageRetryAttempted, setImageRetryAttempted] = useState<boolean>(false);

  // Load charger image if in update mode
  useEffect(() => {
    const loadChargerImage = async () => {
      // Use isEditMode (from route params) not editChargerStatus (from hook)
      if (!isEditMode) {
        setImageLoading(false);
        return;
      }

      // Start loading
      setImageLoading(true);
      setChargerImageUri(null);

      // Wait for form uuid to be available from the hook
      if (!form?.uuid) {
        // Keep loading until we have the uuid
        return;
      }

      // Now we have uuid, load the image
      try {
        // Check cache first
        const cacheKey = `${form.uuid}_original`;
        const cachedUri = await getCachedImageUri(cacheKey);
        
        if (cachedUri) {
          // Use cached version
          setChargerImageUri(cachedUri);
          setImageLoading(false);
          
          // Verify in background
          setTimeout(async () => {
            if (!form?.uuid) return;
            try {
              const uri = await loadChargerThumbnail(form.uuid, 'original', false);
              if (uri && uri !== cachedUri) {
                setChargerImageUri(uri);
              }
            } catch (error) {
              // Background refresh failed, keep cached version
            }
          }, 100);
          return;
        }
        
        // No cache, load from server
        const uri = await loadChargerThumbnail(form.uuid, 'original');
        
        // Set whatever we got (could be null)
        setChargerImageUri(uri);
        setImageLoading(false);
      } catch (error) {
        // Error loading image, fall back to default
        setChargerImageUri(null);
        setImageLoading(false);
      }
    };

    loadChargerImage();
  }, [isEditMode, form?.uuid]); // Use isEditMode not editChargerStatus

  // Handle image load errors
  const handleImageError = async (e: any) => {
    const errorMessage = e.nativeEvent?.error || '';
    
    // Check if this is a file system error
    const isFileSystemError = 
      typeof errorMessage === 'string' && 
      (errorMessage.includes("couldn't be opened") || 
       errorMessage.includes("No such file") ||
       errorMessage.includes("file not found"));
    
    // If stale cache error, retry
    if (form?.uuid && chargerImageUri && (isFileSystemError || !imageRetryAttempted)) {
      setImageRetryAttempted(true);
      setImageLoading(true);
      
      try {
        // Invalidate cache
        await invalidateCacheEntry(`${form.uuid}_original`);
        await invalidateCacheEntry(form.uuid);
        await invalidateCacheEntry(`${form.uuid}_medium`);
        
        // Retry with force refresh
        const uri = await loadChargerThumbnail(form.uuid, 'original', true);
        setChargerImageUri(uri); // null or valid URI
      } catch (error) {
        // Retry failed, fall back to default
        setChargerImageUri(null);
      } finally {
        setImageLoading(false);
      }
    } else {
      // Fall back to default
      setChargerImageUri(null);
      setImageLoading(false);
    }
  };

  return (
    <SafeAreaView style={{ flex: 1, backgroundColor: "#0B1E1D" }}>
      <View
        style={{
          height: height - 65,
          paddingBottom: 10,
          flex: 1,
          backgroundColor: "#0B1E1D",
        }}
      >
        {Platform.OS !== "web" && (
          <Header
            headerText={editChargerStatus ? "Update Charger" : "Add Charger"}
          />
        )}

        <ScrollView>
          {/* SIMPLIFIED: Spinner while loading, then show image (custom or default) */}
          {(() => {
            if (imageLoading) {
              return (
                <View
                  style={
                    Platform.OS === "web"
                      ? { aspectRatio: 1, backgroundColor: '#1E1E1E', justifyContent: 'center', alignItems: 'center' }
                      : {
                          width: "100%",
                          height: 300,
                          backgroundColor: '#1E1E1E',
                          justifyContent: 'center',
                          alignItems: 'center',
                        }
                  }
                >
                  <ActivityIndicator size="large" color="#51B868" />
                </View>
              );
            }
            
            // Not loading - show the image (custom or default)
            const imageSource = chargerImageUri 
              ? { uri: chargerImageUri }
              : require("../../assets/RegisterBgImage.png");
            
            return (
              <View style={{ position: "relative", marginBottom: 0 }}>
                <Image
                  source={imageSource}
                  style={
                    Platform.OS === "web"
                      ? { aspectRatio: 1 }
                      : {
                          width: "100%",
                          height: 300,
                        }
                  }
                  resizeMode="cover"
                  alt={chargerImageUri ? "Charger Image" : "Background Image"}
                  onError={handleImageError}
                />
              </View>
            );
          })()}
          <ChargerForm
            isEditing={editChargerStatus}
            state={{
              form,
              errors,
              returnToOnboarding: route.params?.returnToOnboarding,
            }}
            handlers={handlers}
            isUpdating={isUpdating}
            isEnabled={isEnabled}
            toggleEnable={handlers.toggleEnable}
            onCreate={submitCreate}
            onUpdate={submitUpdate}
            billingType={billingType}
            estResidentialPeak={residentialRatePeak}
            estResidentialOffPeak={residentialRateOffPeak}
            rawPeak={rawPeak}
            rawOffPeak={rawOffPeak}
            peakSelection={peakSelection}
            isToggleLoading={isToggleLoading}
            supportsScheduling={supportsScheduling}
            schedulingEnabled={schedulingEnabled}
            isSchedulingPrefLoading={isSchedulingPrefLoading}
            onToggleSchedulingPreference={toggleSchedulingPreference}
          />
          <PopupModalWrapper
            visible={popupState.visible}
            tooltipData={popupState.text}
            buttons={popupState.buttons}
            onClose={handlers.onPopupClose}
          />
          <PopupModalWrapper
            visible={showDisableWarning}
            tooltipData={disableWarningData}
            buttons={[
              { text: "Continue", onPress: handleDisableConfirm },
              { text: "Cancel", onPress: handleDisableCancel }
            ]}
            onClose={handleDisableCancel}
          />
          <StripeSubscriptionHandler
            verified={verified}
            onVerified={() => setVerified(null)}
            returnToOnboarding={route.params?.returnToOnboarding}
          />
        </ScrollView>
      </View>
    </SafeAreaView>
  );
};

export default RegisterUpdateCharger;
